import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, signal } from '@angular/core';

import { Language, SUPPORTED_LANGUAGES, TRANSLATIONS } from './translations';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly document = inject(DOCUMENT);
  private readonly storageKey = 'lumen-hospitality-language';
  private readonly languageLabels: Record<Language, string> = {
    en: 'English',
    it: 'Italiano',
    es: 'Español',
    de: 'Deutsch',
    fr: 'Français',
    pt: 'Português',
    'zh-CN': '简体中文',
    ar: 'العربية'
  };
  private readonly rtlLanguages = new Set<Language>(['ar']);

  readonly language = signal<Language>(this.readInitialLanguage());
  readonly languages: Language[] = [...SUPPORTED_LANGUAGES];

  constructor() {
    effect(() => {
      const language = this.language();
      const direction = this.rtlLanguages.has(language) ? 'rtl' : 'ltr';

      localStorage.setItem(this.storageKey, language);
      this.document.documentElement.lang = language;
      this.document.documentElement.dir = direction;
      this.document.body?.setAttribute('dir', direction);
    });
  }

  setLanguage(language: Language): void {
    this.language.set(language);
  }

  languageLabel(language: Language): string {
    return this.languageLabels[language];
  }

  translate(key: string, params?: Record<string, string | number | null | undefined>): string {
    const dictionary = TRANSLATIONS[this.language()];
    const fallbackDictionary = TRANSLATIONS.en;
    let value = dictionary[key] ?? fallbackDictionary[key] ?? key;

    if (!params) {
      return value;
    }

    for (const [token, tokenValue] of Object.entries(params)) {
      value = value.replaceAll(`{${token}}`, `${tokenValue ?? ''}`);
    }

    return value;
  }

  private readInitialLanguage(): Language {
    const stored = localStorage.getItem(this.storageKey);

    if (this.isSupportedLanguage(stored)) {
      return stored;
    }

    return 'en';
  }

  private isSupportedLanguage(language: string | null): language is Language {
    return SUPPORTED_LANGUAGES.includes(language as Language);
  }
}
